import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui-kit/avatar';
import { useTranslation } from 'react-i18next';
import {
  Ban,
  CheckCircle2,
  FilePlus2,
  FileSignature,
  Loader2,
  PenLine,
  Send,
  Sparkles,
  TriangleAlert,
  XCircle,
  type LucideIcon,
} from 'lucide-react';
import type { OrgContractDocumentEventItem } from '../types/contract-document-event.types';
import type { DocumentStatus } from '../constants/mock-contract-documents';
import { cn } from '@/lib/utils';

// Local view-model for the timeline renderer. Keeps the renderer agnostic
// to the underlying entity shape so future event sources (legacy Timeline
// JSON, manual entries, webhook fan-out) can be mapped through one path.
export interface TimelineEntry {
  status: string;
  timestamp: string;
  note?: string;
  userId: string;
  userName: string;
  userPhoto: string;
  signatoryId?: string;
  email?: string;
  signedTime?: string;
  declinedBy?: string;
}

export const eventToTimelineEntry = (e: OrgContractDocumentEventItem): TimelineEntry => ({
  status: e.EventType,
  timestamp: e.OccurredAt ?? e.CreatedDate ?? '',
  userId: e.ActorUserId ?? '',
  userName: e.ActorUserName ?? '',
  userPhoto: e.ActorUserPhoto ?? '',
  signatoryId: e.SignatoryId,
  email: e.SignatoryEmail,
  signedTime: e.SignedTime,
  declinedBy: e.DeclinedBy,
});

type StatusVisual = {
  icon: LucideIcon;
  className: string;
  railColor: string;
};

const STATUS_VISUAL: Record<DocumentStatus, StatusVisual> = {
  draft: {
    icon: FilePlus2,
    className: 'bg-slate-100 text-slate-600 ring-slate-200',
    railColor: 'bg-slate-300',
  },
  generation_completed: {
    icon: Sparkles,
    className: 'bg-sky-50 text-sky-600 ring-sky-200',
    railColor: 'bg-sky-300',
  },
  preparation_success: {
    icon: PenLine,
    className: 'bg-sky-50 text-sky-600 ring-sky-200',
    railColor: 'bg-sky-300',
  },
  rollout_success: {
    icon: Send,
    className: 'bg-amber-50 text-amber-600 ring-amber-200',
    railColor: 'bg-amber-300',
  },
  signatory_signed_success: {
    icon: FileSignature,
    className: 'bg-emerald-50 text-emerald-600 ring-emerald-200',
    railColor: 'bg-emerald-300',
  },
  signatory_signed_failed: {
    icon: TriangleAlert,
    className: 'bg-orange-50 text-orange-600 ring-orange-200',
    railColor: 'bg-orange-300',
  },
  document_completed: {
    icon: CheckCircle2,
    className: 'bg-green-50 text-green-600 ring-green-200',
    railColor: 'bg-green-400',
  },
  document_cancelled: {
    icon: XCircle,
    className: 'bg-red-50 text-red-600 ring-red-200',
    railColor: 'bg-red-300',
  },
  document_declined: {
    icon: Ban,
    className: 'bg-orange-50 text-orange-600 ring-orange-200',
    railColor: 'bg-orange-300',
  },
  preparation_failed: {
    icon: TriangleAlert,
    className: 'bg-red-50 text-red-600 ring-red-200',
    railColor: 'bg-red-300',
  },
  rollout_failed: {
    icon: TriangleAlert,
    className: 'bg-red-50 text-red-600 ring-red-200',
    railColor: 'bg-red-300',
  },
  archived: {
    icon: FilePlus2,
    className: 'bg-slate-100 text-slate-600 ring-slate-200',
    railColor: 'bg-slate-300',
  },
};

const FALLBACK_VISUAL: StatusVisual = {
  icon: Sparkles,
  className: 'bg-slate-100 text-slate-600 ring-slate-200',
  railColor: 'bg-slate-300',
};

const STATUS_LABEL_KEY: Record<DocumentStatus, string> = {
  draft: 'ORGCONTRACTS_STATUS_DRAFT',
  generation_completed: 'ORGCONTRACTS_STATUS_GENERATION_COMPLETED',
  preparation_success: 'ORGCONTRACTS_STATUS_PREPARATION_SUCCESS',
  rollout_success: 'ORGCONTRACTS_STATUS_ROLLOUT_SUCCESS',
  signatory_signed_success: 'ORGCONTRACTS_STATUS_SIGNATORY_SIGNED_SUCCESS',
  signatory_signed_failed: 'ORGCONTRACTS_STATUS_SIGNATORY_SIGNED_FAILED',
  document_completed: 'ORGCONTRACTS_STATUS_DOCUMENT_COMPLETED',
  document_cancelled: 'ORGCONTRACTS_STATUS_DOCUMENT_CANCELLED',
  document_declined: 'ORGCONTRACTS_STATUS_DOCUMENT_DECLINED',
  preparation_failed: 'ORGCONTRACTS_STATUS_PREPARATION_FAILED',
  rollout_failed: 'ORGCONTRACTS_STATUS_ROLLOUT_FAILED',
  archived: 'ORGCONTRACTS_STATUS_ARCHIVED',
};

const formatTimestamp = (iso?: string) => {
  if (!iso) return '-';
  try {
    return new Date(iso).toLocaleString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
};

const formatRelative = (iso?: string) => {
  if (!iso) return null;
  const target = new Date(iso).getTime();
  if (Number.isNaN(target)) return null;
  const diffMs = Date.now() - target;
  const diffMin = Math.round(diffMs / 60_000);
  if (Math.abs(diffMin) < 1) return 'just now';
  if (Math.abs(diffMin) < 60) return `${diffMin} min ago`;
  const diffHr = Math.round(diffMin / 60);
  if (Math.abs(diffHr) < 24) return `${diffHr} hr ago`;
  const diffDay = Math.round(diffHr / 24);
  if (Math.abs(diffDay) < 30) return `${diffDay} day${Math.abs(diffDay) === 1 ? '' : 's'} ago`;
  return null;
};

const getInitials = (name?: string, email?: string) => {
  const source = (name || email || '').trim();
  if (!source) return 'SY';
  const parts = source.split(/\s+/);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return source.slice(0, 2).toUpperCase();
};

interface TimelineActor {
  name?: string;
  email?: string;
  photo?: string;
  isSystem: boolean;
}

const resolveActor = (entry: TimelineEntry): TimelineActor => {
  if (entry.userName) {
    return { name: entry.userName, photo: entry.userPhoto, isSystem: false };
  }
  if (entry.email) {
    return { email: entry.email, isSystem: false };
  }
  if (entry.declinedBy) {
    return { email: entry.declinedBy, isSystem: false };
  }
  return { isSystem: true };
};

interface ResolvedHeadline {
  title: string;
  subtitle?: string;
}

const resolveHeadline = (
  entry: TimelineEntry,
  t: (key: string, options?: Record<string, unknown>) => string,
): ResolvedHeadline => {
  switch (entry.status) {
    case 'draft':
      return { title: t('ORGCONTRACTS_TIMELINE_TITLE_DRAFT') };
    case 'signatory_signed_success': {
      const who = entry.email || entry.signatoryId || t('ORGCONTRACTS_TIMELINE_A_SIGNATORY');
      return { title: t('ORGCONTRACTS_TIMELINE_TITLE_SIGNED', { who }) };
    }
    case 'signatory_signed_failed': {
      const who = entry.email || entry.signatoryId || t('ORGCONTRACTS_TIMELINE_A_SIGNATORY');
      return { title: t('ORGCONTRACTS_TIMELINE_TITLE_SIGN_FAILED', { who }) };
    }
    case 'document_declined': {
      const who = entry.declinedBy || entry.email || t('ORGCONTRACTS_TIMELINE_A_SIGNATORY');
      return { title: t('ORGCONTRACTS_TIMELINE_TITLE_DECLINED', { who }) };
    }
    default: {
      const labelKey = STATUS_LABEL_KEY[entry.status as DocumentStatus];
      return {
        title: labelKey ? t(labelKey) : entry.status,
      };
    }
  }
};

const TimelineItem = ({
  entry,
  isLast,
  t,
}: {
  entry: TimelineEntry;
  isLast: boolean;
  t: (key: string, options?: Record<string, unknown>) => string;
}) => {
  const visual = STATUS_VISUAL[entry.status as DocumentStatus] ?? FALLBACK_VISUAL;
  const Icon = visual.icon;
  const actor = resolveActor(entry);
  const headline = resolveHeadline(entry, t);
  const relative = formatRelative(entry.timestamp);

  return (
    <li className="relative flex gap-4 pb-6 last:pb-0">
      {!isLast ? (
        <span
          aria-hidden
          className="absolute left-5 top-11 -bottom-1 w-px bg-linear-to-b from-input via-input to-transparent"
        />
      ) : null}

      <div
        className={cn(
          'relative z-10 flex size-10 shrink-0 items-center justify-center rounded-full ring-4',
          visual.className,
        )}
      >
        <Icon className="size-5" />
      </div>

      <div className="min-w-0 flex-1 rounded-lg border border-input bg-card px-4 py-3 shadow-sm">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div className="min-w-0">
            <p className="truncate text-sm font-semibold text-high-emphasis">{headline.title}</p>
            {headline.subtitle ? (
              <p className="truncate text-xs text-medium-emphasis">{headline.subtitle}</p>
            ) : null}
          </div>
          <div className="flex flex-col items-end text-right">
            <span className="text-xs font-medium text-medium-emphasis">
              {formatTimestamp(entry.timestamp)}
            </span>
            {relative ? (
              <span className="text-[11px] text-medium-emphasis/70">{relative}</span>
            ) : null}
          </div>
        </div>

        <div className="mt-3 flex items-center gap-2">
          {actor.isSystem ? (
            <div className="flex items-center gap-2">
              <span className="inline-flex size-6 items-center justify-center rounded-full bg-muted text-[10px] font-bold text-medium-emphasis">
                SY
              </span>
              <span className="text-xs italic text-medium-emphasis">
                {t('ORGCONTRACTS_TIMELINE_SYSTEM')}
              </span>
            </div>
          ) : (
            <>
              <Avatar className="size-6">
                {actor.photo ? (
                  <AvatarImage src={actor.photo} alt={actor.name ?? actor.email} />
                ) : null}
                <AvatarFallback className="text-[10px] font-semibold">
                  {getInitials(actor.name, actor.email)}
                </AvatarFallback>
              </Avatar>
              <span className="truncate text-xs text-medium-emphasis">
                <span className="font-medium text-high-emphasis">
                  {actor.name ?? actor.email}
                </span>
                {actor.name && actor.email ? (
                  <span className="ml-1 text-medium-emphasis/80">· {actor.email}</span>
                ) : null}
              </span>
            </>
          )}
        </div>

        {entry.signedTime && entry.status === 'signatory_signed_success' ? (
          <p className="mt-2 text-xs text-medium-emphasis">
            <span className="font-medium">{t('ORGCONTRACTS_TIMELINE_SIGNED_AT')}:</span>{' '}
            {formatTimestamp(entry.signedTime)}
          </p>
        ) : null}

        {entry.note ? (
          <p className="mt-2 rounded-md bg-muted/50 px-2 py-1 text-xs text-medium-emphasis">
            {entry.note}
          </p>
        ) : null}
      </div>
    </li>
  );
};

interface ContractEventsTimelineProps {
  events: OrgContractDocumentEventItem[];
  isLoading?: boolean;
  emptyTitle?: string;
  emptyHint?: string;
  className?: string;
}

export const ContractEventsTimeline = ({
  events,
  isLoading = false,
  emptyTitle,
  emptyHint,
  className,
}: ContractEventsTimelineProps) => {
  const { t } = useTranslation();

  const entries = events
    .map(eventToTimelineEntry)
    .sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime());

  if (isLoading) {
    return (
      <div
        className={cn(
          'flex flex-col items-center justify-center gap-2 py-12',
          className,
        )}
      >
        <Loader2 className="size-6 animate-spin text-medium-emphasis" />
        <p className="text-xs text-medium-emphasis">{t('ORGCONTRACTS_TIMELINE_LOADING')}</p>
      </div>
    );
  }

  if (entries.length === 0) {
    return (
      <div
        className={cn(
          'flex flex-col items-center justify-center gap-2 py-12 text-center',
          className,
        )}
      >
        <div className="flex size-12 items-center justify-center rounded-full bg-muted">
          <FilePlus2 className="size-5 text-medium-emphasis" />
        </div>
        <p className="text-sm font-medium text-high-emphasis">
          {emptyTitle ?? t('ORGCONTRACTS_TIMELINE_EMPTY')}
        </p>
        <p className="text-xs text-medium-emphasis">
          {emptyHint ?? t('ORGCONTRACTS_TIMELINE_EMPTY_HINT')}
        </p>
      </div>
    );
  }

  return (
    <ul className={cn('relative pt-2 pl-3', className)}>
      {entries.map((entry, idx) => (
        <TimelineItem
          key={`${entry.status}-${entry.timestamp}-${idx}`}
          entry={entry}
          isLast={idx === entries.length - 1}
          t={t}
        />
      ))}
    </ul>
  );
};

export default ContractEventsTimeline;
